import React, { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import { Delete } from "@mui/icons-material";
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CancelIcon from '@mui/icons-material/Cancel';

import DialogModifyEstadias from "./DialogModifyEstadias";
import TicketEntradaForm from "./TicketEntradaForm";
import TicketSalidaForm from "./TicketSalidaForm";
import TicketHistoryTable from "./TicketHistoryTable";
import ConfirmDialog from "../../common/ConfirmDialog";

import { red, green } from "@mui/material/colors";
import {
  TableRow,
  TableCell,
  IconButton,
  Box,
  Button,
  Typography,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@mui/material";

/* helpers moved to utils */
import {
  formatNumber,
  handleDeleteMovimiento,
  actualizarReporteDiarioValor,
  actualizarReporteDiarioBorrar,
  deleteEstadia,
  closePdfPopup,
  openPdfBlob,
  openPdfPopup,
} from "../../../utils/api";
import { set } from "date-fns";
import { getHighlightScrollBehavior } from "../../../utils/createdRecord";
import { getPersistentRowHighlightSx } from "../../../utils/rowHighlights";
import { authenticatedSessionRequestConfig } from "../../../utils/csrf";

const RowTableEstadia = ({ item, setData, handleUpdateData, setLoading, areUserAdmin, highlighted, scrollHighlight, onHighlightLocated, searchHighlighted, highlightSource }) => {
  const rowRef = React.useRef(null);
  const hasScrolledRef = React.useRef(false);
  const theme = useTheme();
  useEffect(() => {
    if (!highlighted || !scrollHighlight || hasScrolledRef.current) return;
    hasScrolledRef.current = true;
    rowRef.current?.scrollIntoView({ behavior: getHighlightScrollBehavior(), block: 'center' });
    onHighlightLocated?.(item.id);
  }, [highlighted, item.id, onHighlightLocated, scrollHighlight]);
  // dialog visibility flags grouped for clarity
  const [dialogs, setDialogs] = useState({
    delete: false,
    modify: false,
    print: false,
    history: false,
    entrada: false,
    salida: false,
    anular: false,
  });

  const [valorModificableParaVer, setValorModificableParaVer] = useState(item.salida ? item.salida.valor_pagado : 0);
  const [mostrarIngreso, setMostrarIngreso] = useState(true);
  const [ticketSeleccionedToNull, setTicketSeleccionedToNull] = useState(null);
  const actionRef = React.useRef(new Set());
  const printingTicketRef = React.useRef(false);
  const [processingAction, setProcessingAction] = useState(null);

  const tickets = useMemo(
    () => (mostrarIngreso ? item.ticketsIngreso : item.ticketsEgreso) || [],
    [item, mostrarIngreso]
  );

  const toggleDialog = useCallback((name, value) => {
    setDialogs((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDeleteEstadia = async (id, movimientoId) => {
    if (actionRef.current.has('delete')) return;
    actionRef.current.add('delete');
    setProcessingAction('delete');
    try {
      setLoading(true);
      await deleteEstadia(id);
      handleDeleteMovimiento(movimientoId);
      actualizarReporteDiarioBorrar(item.fecha_creacion.split(" ")[0]);
      if (item.salida) {
        actualizarReporteDiarioValor(item.salida.valor_pagado, item.fecha_creacion.split(" ")[0]);
      }
      setData((prev) => prev.filter((row) => row.id !== id));
    } catch (err) {
      console.error("Error al eliminar la estadía:", err);
    } finally {
      actionRef.current.delete('delete');
      setProcessingAction(null);
      setLoading(false);
      toggleDialog('delete', false);
    }
  };

  const handleAnularTicket = async () => {
    if (actionRef.current.has('anular')) return;
    actionRef.current.add('anular');
    setProcessingAction('anular');
    try {
      const tipo = mostrarIngreso ? 'ingreso' : 'salida';
      await axios.put(
        `${process.env.REACT_APP_API_URL}/tickets-${tipo}/${ticketSeleccionedToNull.id}/`,
        { anulado: true },
        await authenticatedSessionRequestConfig(process.env.REACT_APP_API_URL, axios.get)
      );
      // notify parent so the memoized tickets list updates
      const updatedTickets = tickets.map((t) =>
        t.id === ticketSeleccionedToNull.id ? { ...t, anulado: true } : t
      );
      const newItem = {
        ...item,
        [mostrarIngreso ? 'ticketsIngreso' : 'ticketsEgreso']: updatedTickets,
      };
      handleUpdateData(newItem);
    } catch (err) {
      console.error('Error al anular el ticket:', err);
    } finally {
      actionRef.current.delete('anular');
      setProcessingAction(null);
      toggleDialog('anular', false);
    }
  };

  const handleOpenPDF = async (ticketId) => {
    if (printingTicketRef.current) return;
    const pdfWindow = openPdfPopup();
    if (!pdfWindow) return;
    printingTicketRef.current = true;
    try {
      const tipo = mostrarIngreso ? 'ingreso' : 'salida';
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/tickets-${tipo}/${ticketId}/`,
        { responseType: 'blob' }
      );
      openPdfBlob(res.data, pdfWindow);
    } catch (err) {
      console.error('Error al abrir PDF:', err);
      closePdfPopup(pdfWindow);
    } finally {
      printingTicketRef.current = false;
    }
  };

  const handleToggle = () => setMostrarIngreso((v) => !v);

  return (
    <>
      <TableRow
        ref={rowRef}
        aria-current={highlighted ? 'true' : undefined}
        data-highlight-source={highlighted ? highlightSource : undefined}
        sx={{
          backgroundColor: item.salida ? green[700] : red[700],
          ...getPersistentRowHighlightSx(theme, highlighted, searchHighlighted),
        }}
      >
        {/* quick utility to render a cell with borders and black text */}
        {[
          item.salida ? 'FUERA' : 'DENTRO',
          `${item.historial.tipo_vehiculo} ${item.historial.color}: ${item.historial.placa}`,
          item.cliente
            ? `${item.cliente.nombre} ${item.cliente.apellidos}, DOC: ${item.cliente.documento} CEL: ${item.cliente.celular}`
            : 'Cliente no asignado',
          `F:${item.entrada.fecha_entrada} H: ${item.entrada.hora_entrada}`,
          item.salida
            ? `F:${item.salida.fecha_salida} H: ${item.salida.hora_salida}`
            : 'No ha salido',
          item.salida ? item.salida.tiempo_transcurrido : 'No ha salido',
          item.tarifa.nombre,
          item.salida ? `$ ${formatNumber(parseFloat(valorModificableParaVer).toFixed(2))}` : 'No ha salido',
          item.entrada.observaciones,
          item.salida ? item.salida.observaciones : 'No ha salido',
          item.usuario_entrada ? item.usuario_entrada.nombre : 'No Existe Más Este Usuario',
          item.salida
            ? item.usuario_salida
              ? item.usuario_salida.nombre
              : 'No Existe Más Este Usuario'
            : 'No ha salido',
        ].map((text, idx) => (
          <TableCell
            key={idx}
            align={idx > 0 ? 'left' : undefined}
            sx={{
              borderLeft: idx > 0 ? '2px solid #ddd' : undefined,
              borderRight: idx === 11 && !areUserAdmin ? '2px solid #ddd' : undefined,
              borderBottom: '2px solid #ddd',
              color: 'black',
            }}
          >
            {text}
          </TableCell>
        ))}

        <TableCell
          sx={{
            borderLeft: '2px solid #ddd',
            borderRight: '2px solid #ddd',
            borderBottom: '2px solid #ddd',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <IconButton size="large" sx={{ color: '#grey' }} onClick={() => toggleDialog('print', true)}>
              <PrintIcon sx={{ fontSize: 33 }} />
            </IconButton>
            <IconButton
              size="large"
              sx={{ color: '#grey' }}
              onClick={() => toggleDialog('history', !dialogs.history)}
            >
              <ConfirmationNumberIcon sx={{ fontSize: 33 }} />
            </IconButton>
            {areUserAdmin && (
              <>
                <IconButton size="large" sx={{ color: '#grey' }} onClick={() => toggleDialog('modify', true)}>
                  <EditIcon sx={{ fontSize: 33 }} />
                </IconButton>
                <IconButton size="large" sx={{ color: '#grey' }} onClick={() => toggleDialog('delete', true)}>
                  <Delete sx={{ fontSize: 33 }} />
                </IconButton>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>

      {/* delete confirmation */}
      {areUserAdmin && (
        <ConfirmDialog
          open={dialogs.delete}
          title="Espera un Segundo!"
          message="Estás apunto de eliminar esta Estadia del Registro de Estadias de este parqueadero ¿Estás seguro de que quieras hacer esto?"
          onCancel={() => toggleDialog('delete', false)}
          onConfirm={() => handleDeleteEstadia(item.id, item.movimiento)}
          processing={processingAction === 'delete'}
        />
      )}

      {/* print selector dialog */}
      <Dialog open={dialogs.print} onClose={() => toggleDialog('print', false)}>
        <DialogTitle>Seleccionar tipo de impresión</DialogTitle>
        <DialogContent>
          <DialogContentText paddingTop={0}>
            ¿Qué ticket deseas imprimir?
          </DialogContentText>
          {!item.salida && (
            <Typography variant="caption" color="text.secondary">
              Registre primero la salida.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              toggleDialog('entrada', true);
              toggleDialog('print', false);
            }}
          >
            Ticket de Entrada
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!item.salida}
            onClick={() => {
              toggleDialog('salida', true);
              toggleDialog('print', false);
            }}
          >
            Ticket de Salida
          </Button>
        </DialogActions>
      </Dialog>

      <TicketEntradaForm
        openTicketEntradaForm={dialogs.entrada}
        setOpenTicketEntradaForm={(v) => toggleDialog('entrada', v)}
        data={item}
        areUserAdmin={areUserAdmin}
        handleUpdateData={handleUpdateData}
      />
      {item.salida && (
        <TicketSalidaForm
          openTicketSalidaForm={dialogs.salida}
          setOpenTicketSalidaForm={(v) => toggleDialog('salida', v)}
          data={item}
          areUserAdmin={areUserAdmin}
          handleUpdateData={handleUpdateData}
          setValorModificableParaVer={setValorModificableParaVer}
          valorModificableParaVer={valorModificableParaVer}
        />
      )}
      {areUserAdmin && (
        <DialogModifyEstadias
          handleCloseDialog={() => toggleDialog('modify', false)}
          openDialog={dialogs.modify}
          data={item}
          handleUpdateData={handleUpdateData}
          setValorModificableParaVer={setValorModificableParaVer}
        />
      )}

      {/* ticket history collapse */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14}>
          <Collapse in={dialogs.history} timeout="auto" unmountOnExit>
            <TicketHistoryTable
              tickets={tickets}
              mostrarIngreso={mostrarIngreso}
              item={item}
              onToggle={handleToggle}
              onAnular={(t) => setTicketSeleccionedToNull(t)}
              onPrint={handleOpenPDF}
              openAnular={dialogs.anular}
              setOpenAnular={(v) => toggleDialog('anular', v)}
            />
            {/* anular confirmation is handled inside TicketHistoryTable? we can move it here if needed */}
            {dialogs.anular && (
              <ConfirmDialog
                open={dialogs.anular}
                title="Espera un Segundo!"
                message={`Estás a punto de anular este ticket de ${mostrarIngreso ? 'Ingreso' : 'Salida'} del Registro de Tickets de este parqueadero`}
                onCancel={() => toggleDialog('anular', false)}
                onConfirm={handleAnularTicket}
                processing={processingAction === 'anular'}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// memoize rows to avoid re‑rendering unchanged items
export default React.memo(RowTableEstadia);
